import { useEffect, useId, useRef, type ReactNode } from "react";
import { motion } from "motion/react";
import { Link } from "wouter";
import { Card, Field, ToneDot, cn, spring } from "../../ui/index.ts";

export type NavEntry = { id: string; label: string; href: string; icon: ReactNode; attention?: boolean };

/** Settings sections as a vertical list beside the content on desktop, and a scrolling row of tabs above it on smaller screens. */
export function SettingsNav({ items, active }: { items: NavEntry[]; active: string }) {
  const nav = useRef<HTMLElement>(null);

  useEffect(() => {
    const n = nav.current;
    const a = n?.querySelector('[aria-current="page"]');
    if (!n || !a || n.scrollWidth <= n.clientWidth) return;
    const box = n.getBoundingClientRect();
    const tab = a.getBoundingClientRect();
    if (tab.left < box.left + 16) n.scrollBy({ left: tab.left - box.left - 16, behavior: "smooth" });
    else if (tab.right > box.right - 16) n.scrollBy({ left: tab.right - box.right + 16, behavior: "smooth" });
  }, [active]);

  return (
    <nav ref={nav} aria-label="Settings" className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] lg:sticky lg:top-10 lg:mx-0 lg:overflow-visible lg:px-0">
      <ul className="flex w-max gap-1 border-b border-line lg:w-auto lg:flex-col lg:gap-0.5 lg:border-0">
        {items.map((item) => {
          const on = item.id === active;
          return (
            <li key={item.id} className="relative">
              <Link
                href={item.href}
                aria-current={on ? "page" : undefined}
                className={cn(
                  "relative flex h-10 items-center gap-2 whitespace-nowrap px-3 text-sm font-semibold transition-[background-color,color] duration-150 lg:h-9 lg:rounded-control",
                  on ? "text-primary-ink" : "text-ink-soft hover:text-ink lg:hover:bg-sunk",
                )}
              >
                {on ? (
                  <>
                    <motion.span layoutId="settings-tab" className="absolute inset-x-0 -bottom-px h-0.5 bg-primary lg:hidden" transition={spring} />
                    <motion.span layoutId="settings-pill" className="absolute inset-0 hidden rounded-control bg-primary-soft lg:block" transition={spring} />
                  </>
                ) : null}
                <span className={cn("relative inline-flex transition-[color] duration-150", on ? "text-primary" : "text-muted")} aria-hidden>
                  {item.icon}
                </span>
                <span className="relative flex-1">{item.label}</span>
                {item.attention ? (
                  <>
                    <ToneDot className="relative size-2 text-warn" />
                    <span className="sr-only">, needs attention</span>
                  </>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** One section: a title and a line of context on top, labeled rows in the middle, and a footer for its actions. */
export function SettingsCard({
  title,
  description,
  notice,
  footer,
  children,
}: {
  title: string;
  description: ReactNode;
  notice?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <Card className="overflow-hidden" role="region" aria-labelledby={id}>
      <div className="flex flex-col gap-1 border-b border-line px-5 py-4 md:px-6 md:py-5">
        <h2 id={id} className="text-[17px] font-bold tracking-[-0.015em] text-ink">
          {title}
        </h2>
        <p className="text-sm text-muted">{description}</p>
        {notice ? <div className="mt-3">{notice}</div> : null}
      </div>
      <div className="divide-y divide-line">{children}</div>
      {footer ? <div className="flex flex-wrap items-center justify-end gap-3 border-t border-line bg-sunk/60 px-5 py-3 md:px-6">{footer}</div> : null}
    </Card>
  );
}

/** A labeled setting: label and hint on the left with the control on the right at wide widths, stacked below that. */
export function Row({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <Field
      label={
        <>
          {label}
          {hint ? <span className="mt-0.5 block text-[13px] font-normal text-muted">{hint}</span> : null}
        </>
      }
      className="gap-2 px-5 py-4 md:grid md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] md:gap-x-8 md:px-6 md:py-5 md:[&>label]:pt-2.5 md:[&>p]:col-start-2"
    >
      <div className="min-w-0 max-w-xl">{children}</div>
    </Field>
  );
}

/** A short warning at the top of a section, e.g. what autopilot still needs from it. */
export function Notice({ children }: { children: ReactNode }) {
  return <p className="rounded-control bg-tone-amber px-3 py-2 text-[13px] font-semibold text-tone-amber-ink">{children}</p>;
}
